import AddIcon from "@mui/icons-material/Add";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, LinearProgress, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";

const SOURCE_OPTIONS = [
  ["enjeux", "Enjeux"], ["pip", "PIP"], ["audit_interne", "Audit interne"],
  ["audit_externe", "Audit externe"], ["aide_memoire", "Aide Mémoire"],
  ["objectif_qualite", "Objectif Qualité"], ["nc_interne", "NC interne"],
  ["plan_action", "Plan d'action"],
];
const NATURE_OPTIONS = [
  ["preventive", "Préventive"], ["curative", "Curative"],
  ["corrective", "Corrective"],
];
const EFFICACY_OPTIONS = [
  ["not_evaluated", "Non évaluée"], ["effective", "Efficace"],
  ["ineffective", "Inefficace"],
];
const CLOSURE_OPTIONS = [["open", "Ouvert"], ["closed", "Clôturé"]];
const EMPTY_ACTION = {
  action: "", closure: "open", creationDate: "", deadline: "",
  description: "", efficacy: "not_evaluated", issuer: "", nature: "preventive",
  origin: "", progress: 0, recommendation: "", reference: "",
  responsible: "", rootCause: "",
};
const SEARCH_FIELDS = [
  "origin", "reference", "creationDate", "description", "issuer", "action",
  "progress", "nature", "responsible", "deadline", "efficacy", "closure",
];
const COLUMNS = [
  ["Origine", 125], ["Réf. NC", 125], ["Date", 105],
  ["Description", 190], ["Émetteur", 135], ["Action", 175],
  ["Avancement", 135], ["Nature", 110], ["Responsable", 145],
  ["Échéance", 110], ["Efficacité", 115], ["Clôture", 105],
  ["Actions", 100],
];
const normalize = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");
const createId = () => `treatment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getLabel = (options, key) =>
  options.find(([value]) => value === key)?.[1] || key || "—";

const textareaSx = {
  backgroundColor: "#fff", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "6px", boxSizing: "border-box", color: OUTIL_COLORS.text,
  fontFamily: "'DM Sans', sans-serif", fontSize: 11.5, lineHeight: 1.45,
  minHeight: 68, outline: "none", p: 1, resize: "vertical", width: "100%",
  "&:focus": { borderColor: OUTIL_COLORS.navy, boxShadow: "0 0 0 2px rgba(23,61,97,.12)" },
};
const goldButtonSx = {
  backgroundColor: OUTIL_COLORS.gold, color: OUTIL_COLORS.text,
  fontSize: 10, fontWeight: 700, textTransform: "none",
  "&:hover": { backgroundColor: "#c79c2f" },
};
const sectionSx = {
  backgroundColor: "#f8fafc", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "8px", p: 1.4,
};

const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    color="#526b84" component="label" display="block" fontSize={9.5}
    fontWeight={700} htmlFor={htmlFor} letterSpacing=".04em" mb={0.45}
    textTransform="uppercase"
  >
    {children}
  </Typography>
);
const InputField = ({ field, label, onChange, type = "text", value }) => (
  <Box>
    <FieldLabel htmlFor={`treatment-${field}`}>{label}</FieldLabel>
    <TextField
      fullWidth id={`treatment-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      size="small" type={type} value={value ?? ""}
    />
  </Box>
);
const TextareaField = ({ field, label, onChange, value }) => (
  <Box>
    <FieldLabel htmlFor={`treatment-${field}`}>{label}</FieldLabel>
    <Box
      aria-label={label} component="textarea" id={`treatment-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      rows={3} sx={textareaSx} value={value || ""}
    />
  </Box>
);
const SelectField = ({ field, label, onChange, options, value }) => (
  <Box>
    <FieldLabel htmlFor={`treatment-${field}`}>{label}</FieldLabel>
    <TextField
      fullWidth id={`treatment-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      select size="small" value={value || options[0][0]}
    >
      {options.map(([key, optionLabel]) => (
        <MenuItem key={key} value={key}>{optionLabel}</MenuItem>
      ))}
    </TextField>
  </Box>
);
const FormSection = ({ children, icon, title }) => (
  <Paper elevation={0} sx={sectionSx}>
    <Typography
      color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={800}
      sx={{ borderBottom: `1px solid ${OUTIL_COLORS.border}`, mb: 1.2, pb: 0.75 }}
      textTransform="uppercase"
    >
      {icon}{title}
    </Typography>
    {children}
  </Paper>
);

const ActionDialog = ({ form, onChange, onClose, onDelete, onSave, open }) => (
  <Dialog
    fullWidth maxWidth="md" onClose={onClose} open={open}
    PaperProps={{ sx: { borderRadius: "14px", maxHeight: "94vh" } }}
  >
    <DialogTitle sx={{ alignItems: "center", borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, justifyContent: "space-between", px: 2.2, py: 1.4 }}>
      <Box component="span">
        <DescriptionOutlinedIcon sx={{ color: "#d0783a", fontSize: 19, mr: 0.8, verticalAlign: "middle" }} />
        Suivi plan d'action — Fiche NC/Action
      </Box>
      <Button color="error" onClick={onClose} size="small" startIcon={<CloseIcon />} sx={{ textTransform: "none" }} variant="outlined">Fermer</Button>
    </DialogTitle>
    <DialogContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "grid", gap: 1.2 }}>
        <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#edf3f8", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "7px", display: "flex", justifyContent: "space-between", px: 1.4, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            {form.id ? "Modification de l'entrée sélectionnée" : "Nouvelle NC / action"}
          </Typography>
          {form.id && <Button color="error" onClick={onDelete} size="small" startIcon={<DeleteOutlineIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Supprimer cette entrée</Button>}
        </Paper>

        <FormSection icon={<AttachFileOutlinedIcon sx={{ fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Pièces jointes (0)">
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Aucune pièce jointe</Typography>
          <Tooltip title="Ajout de pièce jointe disponible ultérieurement">
            <span><Button disabled size="small" startIcon={<AttachFileOutlinedIcon />} sx={{ fontSize: 10.5, mt: 0.5, textTransform: "none" }}>Ajouter une pièce jointe</Button></span>
          </Tooltip>
        </FormSection>

        <FormSection icon={<EditOutlinedIcon sx={{ color: "#d49b17", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Identification">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <InputField field="reference" label="Référence NC" onChange={onChange} value={form.reference} />
            <InputField field="creationDate" label="Date de création" onChange={onChange} type="date" value={form.creationDate} />
            <SelectField field="origin" label="Origine / source" onChange={onChange} options={SOURCE_OPTIONS} value={form.origin} />
            <InputField field="issuer" label="Émetteur" onChange={onChange} value={form.issuer} />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <TextareaField field="description" label="Description de la non-conformité" onChange={onChange} value={form.description} />
            </Box>
          </Box>
        </FormSection>

        <FormSection icon={<SearchIcon sx={{ color: "#2f94c4", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Analyse des causes">
          <Box sx={{ display: "grid", gap: 1.1 }}>
            <TextareaField field="rootCause" label="Cause racine identifiée" onChange={onChange} value={form.rootCause} />
            <TextareaField field="recommendation" label="Recommandation" onChange={onChange} value={form.recommendation} />
          </Box>
        </FormSection>

        <FormSection icon={<TrackChangesOutlinedIcon sx={{ color: "#d54873", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Plan d'action corrective">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <TextareaField field="action" label="Nature de l'action" onChange={onChange} value={form.action} />
            </Box>
            <InputField field="responsible" label="Responsable" onChange={onChange} value={form.responsible} />
            <InputField field="deadline" label="Échéance" onChange={onChange} type="date" value={form.deadline} />
            <SelectField field="nature" label="Type d'action" onChange={onChange} options={NATURE_OPTIONS} value={form.nature} />
            <InputField field="progress" label="Avancement (%)" onChange={onChange} type="number" value={form.progress} />
            <SelectField field="efficacy" label="Efficacité" onChange={onChange} options={EFFICACY_OPTIONS} value={form.efficacy} />
            <SelectField field="closure" label="Clôture" onChange={onChange} options={CLOSURE_OPTIONS} value={form.closure} />
          </Box>
        </FormSection>
      </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 2.2, py: 1.25 }}>
      <Button onClick={onClose} sx={{ color: OUTIL_COLORS.text, textTransform: "none" }}>Annuler</Button>
      <Button onClick={onSave} startIcon={<SaveOutlinedIcon />} sx={{ backgroundColor: "#27884f", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }} variant="contained">Valider et sauvegarder</Button>
    </DialogActions>
  </Dialog>
);

const FilterChip = ({ active, label, onClick }) => (
  <Chip
    clickable
    color={active ? "primary" : "default"}
    label={label}
    onClick={onClick}
    size="small"
    sx={{
      border: `1px solid ${active ? OUTIL_COLORS.navy : OUTIL_COLORS.border}`,
      fontSize: 9.5, fontWeight: active ? 700 : 500,
    }}
    variant={active ? "filled" : "outlined"}
  />
);

const SuiviTraitement = ({ onChange, value = {} }) => {
  const [sourceFilter, setSourceFilter] = useState("");
  const [natureFilter, setNatureFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_ACTION });
  const rows = useMemo(
    () => (Array.isArray(value.treatmentRows) ? value.treatmentRows : []),
    [value.treatmentRows]
  );
  const filteredRows = useMemo(() => {
    const query = normalize(search).trim();
    return rows.filter((row) => {
      if (sourceFilter && row.origin !== sourceFilter) return false;
      if (natureFilter && row.nature !== natureFilter) return false;
      return !query || SEARCH_FIELDS.some((field) => normalize(row[field]).includes(query));
    });
  }, [natureFilter, rows, search, sourceFilter]);
  const dates = useMemo(
    () => rows.map((row) => row.creationDate).filter(Boolean).sort(),
    [rows]
  );

  const setRows = (nextRows) => onChange("treatmentRows", nextRows);
  const openCreate = () => {
    setForm({ ...EMPTY_ACTION });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY_ACTION, ...row });
    setDialogOpen(true);
  };
  const saveAction = () => {
    const progress = Math.min(100, Math.max(0, Number(form.progress) || 0));
    const saved = { ...form, progress };
    setRows(form.id
      ? rows.map((row) => (row.id === form.id ? saved : row))
      : [...rows, { ...saved, id: createId() }]);
    setDialogOpen(false);
  };
  const deleteAction = () => {
    if (form.id) setRows(rows.filter((row) => row.id !== form.id));
    setDialogOpen(false);
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Paper aria-label="Filtres du suivi de traitement" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 48, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            Vue combinée — Toutes sources NC et plans d'action
          </Typography>
          <Chip label="Vue globale tous processus" size="small" sx={{ fontSize: 9.5 }} />
        </Box>
        <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 0.75, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>FILTRER PAR SOURCE :</Typography>
          <FilterChip active={!sourceFilter} label="Toutes" onClick={() => setSourceFilter("")} />
          {SOURCE_OPTIONS.map(([key, label]) => <FilterChip active={sourceFilter === key} key={key} label={label} onClick={() => setSourceFilter(key)} />)}
          <Box sx={{ borderLeft: `1px solid ${OUTIL_COLORS.border}`, height: 24, mx: 0.5 }} />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>NATURE :</Typography>
          {NATURE_OPTIONS.map(([key, label]) => <FilterChip active={natureFilter === key} key={key} label={label} onClick={() => setNatureFilter(natureFilter === key ? "" : key)} />)}
        </Box>
        <Box sx={{ backgroundColor: "#edf3f8", borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 1.5, py: 0.9 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5}>
            CHRONOLOGIE : {dates.length ? `${dates[0]} — ${dates[dates.length - 1]}` : "Aucune date saisie"}
          </Typography>
        </Box>
      </Paper>

      <Paper aria-label="Suivi de traitement des plans d'action" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 48, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <DescriptionOutlinedIcon sx={{ color: "#d0783a", fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Suivi de traitement des plans d'action — §10.2
          </Typography>
          <Button onClick={openCreate} size="small" startIcon={<AddIcon />} sx={goldButtonSx} variant="contained">Ajouter NC/Action</Button>
        </Box>
        <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
          <TextField
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer toutes les colonnes..." size="small"
            sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
            value={search}
          />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} / {rows.length} entrée(s)</Typography>
          <Button
            disabled={!search && !sourceFilter && !natureFilter}
            onClick={() => { setSearch(""); setSourceFilter(""); setNatureFilter(""); }}
            size="small" startIcon={<RestartAltIcon />}
            sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}
          >
            Réinit.
          </Button>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Tableau du suivi de traitement" size="small" sx={{ minWidth: 1750, tableLayout: "fixed" }}>
            <TableHead><TableRow>{COLUMNS.map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {!filteredRows.length ? (
                <TableRow><TableCell align="center" colSpan={COLUMNS.length} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucune NC ou action enregistrée.</TableCell></TableRow>
              ) : filteredRows.map((row) => (
                <TableRow hover key={row.id} onClick={() => openEdit(row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                  <TableCell>{getLabel(SOURCE_OPTIONS, row.origin)}</TableCell>
                  <TableCell>{row.reference || "—"}</TableCell>
                  <TableCell>{row.creationDate || "—"}</TableCell>
                  <TableCell>{row.description || "—"}</TableCell>
                  <TableCell>{row.issuer || "—"}</TableCell>
                  <TableCell>{row.action || "—"}</TableCell>
                  <TableCell>
                    <Box sx={{ alignItems: "center", display: "flex", gap: 0.7 }}>
                      <LinearProgress sx={{ borderRadius: 3, height: 5, minWidth: 68 }} value={Number(row.progress) || 0} variant="determinate" />
                      <Typography fontSize={9.5}>{Number(row.progress) || 0}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={getLabel(NATURE_OPTIONS, row.nature)} size="small" sx={{ backgroundColor: "#e3f3e8", color: "#217743", fontSize: 9.5 }} /></TableCell>
                  <TableCell>{row.responsible || "—"}</TableCell>
                  <TableCell>{row.deadline || "—"}</TableCell>
                  <TableCell><Chip label={getLabel(EFFICACY_OPTIONS, row.efficacy)} size="small" sx={{ fontSize: 9.5 }} /></TableCell>
                  <TableCell><Chip label={getLabel(CLOSURE_OPTIONS, row.closure)} size="small" sx={{ backgroundColor: row.closure === "closed" ? "#d9f7e5" : "#fde7e7", color: row.closure === "closed" ? "#147a43" : "#a92824", fontSize: 9.5 }} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Entrée sauvegardée localement">
                        <IconButton aria-label="Entrée sauvegardée localement" onClick={(event) => event.stopPropagation()} size="small" sx={{ backgroundColor: "#27884f", borderRadius: "6px", color: "#fff" }}><SaveOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton aria-label="Modifier le suivi" onClick={(event) => { event.stopPropagation(); openEdit(row); }} size="small" sx={{ backgroundColor: OUTIL_COLORS.navy, borderRadius: "6px", color: "#fff" }}><EditOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ActionDialog
        form={form}
        onChange={(field, nextValue) => setForm((current) => ({ ...current, [field]: nextValue }))}
        onClose={() => setDialogOpen(false)}
        onDelete={deleteAction}
        onSave={saveAction}
        open={dialogOpen}
      />
    </Box>
  );
};

export default SuiviTraitement;